import React, { useCallback, useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { usePointLocation } from '../../../hooks/hook.location';
import LoadingPage from '../../LoadingPage';
import {
  ALL_ACTIVE_PLACES,
  DEFAULT_LOCATION,
  getSeoApproximateLocation,
  getSeoLocation,
  isActiveCountry,
  LocationFullType,
} from '../../../components/Location/config';
import { useIonRouter } from '@ionic/react';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import {
  getAutocomplePredictions,
  getCountryInfo,
  getGeocodeResults,
  getLocationFromActivePlace,
  getNearestActiveLocation,
} from '../../../components/Location/helpers';
import { getLatLng } from 'use-places-autocomplete';
import { locationActionCreators } from '../../../redux/slices/location/actionCreators';

const UserCatalogLocationResolver = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { push, routeInfo } = useIonRouter();

  const searchParams = new URLSearchParams(routeInfo.search);

  const match = useRouteMatch<{ locationName: string }>();

  const pointLocation = usePointLocation();
  const lockedLocation = useTypedSelector(s => s.location.lockedLocation);

  // represent same
  const locationName = match.params.locationName;
  const placeId = searchParams.get('place_id');

  const predefinedLocation = useMemo(() => {
    const activePlace = ALL_ACTIVE_PLACES.find(
      p => p.seoLine.toLowerCase() === locationName.toLowerCase()
    );
    if (activePlace) {
      return {
        ...getLocationFromActivePlace(activePlace),
        seoLine: activePlace.seoLine,
      };
    }
  }, [locationName]);

  const dispatch = useTypedDispatch();
  const lastPlaceId = useTypedSelector(s => s.location.lastPlaceId);
  const setLastPlaceId = useCallback(
    (placeId: string) =>
      dispatch(locationActionCreators.setLastPlaceId(placeId)),
    [dispatch]
  );

  const changeRouter = useCallback(
    ({ seoLine, placeId }: { seoLine: string; placeId?: string }) => {
      const newPathname = routeInfo.pathname.replace(
        /^\/catalog\/([^\/]+)/,
        `/catalog/${seoLine}`
      );

      const params = new URLSearchParams(window.location.search);

      if (placeId) {
        params.set('place_id', placeId);
      } else {
        params.delete('place_id');
      }

      const p = params.toString();
      const newUrl = newPathname + (p ? `?${p}` : '');

      console.log('change to', newUrl);
      push(newUrl, 'none', 'pop');
    },
    [push, routeInfo.pathname]
  );

  useEffect(() => {
    (async () => {
      if (!pointLocation) return;
      if (!locationName) return;

      if (lockedLocation) {
        setLastPlaceId(lockedLocation.place_id);
        return;
      }

      if (!lastPlaceId) {
        // Executed only during initial load

        // First load, if data "locationName" is in pre-defined config and no exact placeId specified
        if (predefinedLocation && !placeId) {
          dispatch(locationActionCreators.selectLocation(predefinedLocation));
          setLastPlaceId(predefinedLocation.place_id);
          return;
        }

        try {
          const results = await getGeocodeResults({
            placeId,
            address: !placeId ? locationName.replace(/-/g, ' ') : undefined,
          });

          const { country, state, city } = getCountryInfo(results);
          const coordinates = getLatLng(results[0]);

          const { nearestActiveLocation, nearestActiveLocationSeoLine } =
            getNearestActiveLocation({
              coordinates,
              country,
            });

          if (!isActiveCountry(country)) {
            // CASE: Set system to nearest active location in another country
            dispatch(
              locationActionCreators.selectLocation(nearestActiveLocation)
            );
            changeRouter({ seoLine: nearestActiveLocationSeoLine });
            setLastPlaceId(nearestActiveLocation.place_id);
          } else {
            // CASE: Set system to actual location within same country
            const placeName = results[0].formatted_address;
            const prediction = (
              await getAutocomplePredictions(placeName, true)
            )[0];

            const main = prediction.structured_formatting.main_text;
            const secondary = prediction.structured_formatting.secondary_text;
            const locationFromUrl: LocationFullType = {
              ...prediction,
              inputMain: main,
              inputSecondary: secondary,
              coordinates,
              country,
              state,
              city,
            };
            dispatch(locationActionCreators.selectLocation(locationFromUrl));
            setLastPlaceId(locationFromUrl.place_id);
          }
        } catch (err) {
          console.error(err);
          dispatch(locationActionCreators.selectLocation(DEFAULT_LOCATION));
          changeRouter({ seoLine: getSeoLocation(DEFAULT_LOCATION) });
          setLastPlaceId(DEFAULT_LOCATION.place_id);
        }

        return;
      } else if (lastPlaceId !== pointLocation.place_id) {
        const isActivePlace = !!ALL_ACTIVE_PLACES.find(
          p => p.placeId === pointLocation.place_id
        );

        // When user change locaiton
        changeRouter({
          seoLine: getSeoApproximateLocation(pointLocation),
          placeId: !isActivePlace ? pointLocation.place_id : undefined,
        });
        setLastPlaceId(pointLocation.place_id);

        return;
      }
    })();
  }, [
    dispatch,
    predefinedLocation,
    pointLocation,
    locationName,
    placeId,
    lastPlaceId,
    setLastPlaceId,
    lockedLocation,
    changeRouter,
  ]);

  if (!locationName || !pointLocation || !lastPlaceId) return <LoadingPage />;

  return <>{children}</>;
};

export default UserCatalogLocationResolver;
